import { z } from 'zod';
import {
  BUILDING_TYPES,
  CASE_SOURCES,
  LOST_REASONS,
  NOTE_KINDS,
  normalizePhone,
  PRIORITIES,
  SITE_ROLES,
  TRADE_KINDS,
} from './crm';
import { isValidNip, normalizeNip } from './nip';
import './schemas'; // polskie komunikaty zod

const keys = <T extends Record<string, unknown>>(o: T) => Object.keys(o) as [keyof T & string, ...(keyof T & string)[]];
const str = (max: number) => z.string().trim().max(max);
const optStr = (max: number) => str(max).default('');

const phone = z
  .string()
  .trim()
  .max(30)
  .refine((v) => normalizePhone(v) !== null, 'Numer telefonu musi mieć co najmniej 9 cyfr');

const postalCode = z
  .string()
  .trim()
  .refine((v) => v === '' || /^\d{2}-\d{3}$/.test(v), 'Kod pocztowy w formacie 00-000')
  .default('');

const nip = z
  .string()
  .trim()
  .transform((v) => (v === '' ? '' : normalizeNip(v)))
  .refine((v) => v === '' || /^\d{10}$/.test(v), 'NIP musi mieć 10 cyfr')
  .refine((v) => v === '' || !/^\d{10}$/.test(v) || isValidNip(v), 'Niepoprawny NIP — błędna cyfra kontrolna')
  .default('');

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data w formacie RRRR-MM-DD')
  .nullable();

export const partyInputSchema = z
  .object({
    kind: z.enum(['osoba', 'firma']),
    firstName: optStr(80),
    lastName: optStr(80),
    name: optStr(200),
    nip,
    street: optStr(120),
    postalCode,
    city: optStr(80),
    phones: z.array(phone).max(3).default([]),
    emails: z.array(z.string().trim().toLowerCase().email('Niepoprawny adres e-mail')).max(3).default([]),
    consentReminders: z.boolean().default(true),
    consentMarketing: z.boolean().default(false),
    notes: optStr(2000),
  })
  .superRefine((p, ctx) => {
    if (p.kind === 'osoba' && p.lastName.length < 2) ctx.addIssue({ code: 'custom', path: ['lastName'], message: 'Podaj nazwisko' });
    if (p.kind === 'firma' && p.name.length < 2) ctx.addIssue({ code: 'custom', path: ['name'], message: 'Podaj nazwę firmy' });
  });
export type PartyInput = z.infer<typeof partyInputSchema>;

export const partyCreateSchema = z.object({ party: partyInputSchema, force: z.boolean().default(false) });

export const contactInputSchema = z.object({
  fullName: str(120).min(2, 'Podaj imię i nazwisko'),
  role: optStr(60),
  phone: z.union([z.literal(''), phone]).default(''),
  email: z.union([z.literal(''), z.string().trim().toLowerCase().email('Niepoprawny adres e-mail')]).default(''),
  notes: optStr(500),
});
export type ContactInput = z.infer<typeof contactInputSchema>;

export const siteInputSchema = z.object({
  street: optStr(120),
  postalCode,
  city: str(80).min(2, 'Podaj miejscowość'),
  buildingType: z.enum(keys(BUILDING_TYPES)).nullable().default(null),
  usableArea: z.number().positive().max(100000).nullable().default(null),
  accessNotes: optStr(1000),
  technicalNotes: optStr(2000),
  lat: z.number().min(-90).max(90).nullable().default(null),
  lng: z.number().min(-180).max(180).nullable().default(null),
});
export type SiteInput = z.infer<typeof siteInputSchema>;

export const siteCreateSchema = z.object({
  site: siteInputSchema,
  partyId: z.string().uuid(),
  role: z.enum(keys(SITE_ROLES)).default('wlasciciel'),
});

export const siteLinkSchema = z.object({ partyId: z.string().uuid(), role: z.enum(keys(SITE_ROLES)) });

export const scopeSpecSchema = z.object({ tradeCode: str(4).min(2), companyId: z.string().uuid() });

/** Szybkie zapytanie: klient i lokalizacja istniejące albo nowe, branże z firmami. */
export const quickCaseSchema = z.object({
  client: z.union([z.object({ id: z.string().uuid() }), z.object({ party: partyInputSchema })]),
  site: z.union([z.object({ id: z.string().uuid() }), z.object({ site: siteInputSchema })]),
  scopes: z.array(scopeSpecSchema).min(1, 'Wybierz co najmniej jedną branżę'),
  description: optStr(4000),
  source: z.enum(keys(CASE_SOURCES)),
  priority: z.enum(keys(PRIORITIES)).default('normalny'),
  ownerId: z.string().uuid().nullable().default(null),
  /** bez opiekuna — sprawa trafia do kolejki „do przejęcia” z weryfikacją wstępną (QA P17); ownerId pomijane */
  toQueue: z.boolean().optional(),
  onsiteContact: contactInputSchema.nullable().default(null),
  force: z.boolean().default(false),
});
export type QuickCaseInput = z.infer<typeof quickCaseSchema>;

export const caseUpdateSchema = z.object({
  title: str(200).min(3, 'Tytuł musi mieć co najmniej 3 znaki'),
  description: optStr(4000),
  source: z.enum(keys(CASE_SOURCES)),
  priority: z.enum(keys(PRIORITIES)),
  investorId: z.string().uuid().nullable(),
  onsiteContactId: z.string().uuid().nullable(),
  nextAction: optStr(300),
  nextActionDate: date,
});
export type CaseUpdateInput = z.infer<typeof caseUpdateSchema>;

export const caseOwnerSchema = z.object({ ownerId: z.string().uuid() });
/**
 * Zamknięcie sprawy bez realizacji: odrzucone zapytanie (powód opisowy) albo przegrana — powód z listy utraconej sprzedaży
 * (żywe zakresy zostają oznaczone jako przegrane); opcjonalnie e-mail do klienta z powodem (P34).
 */
export const caseCloseSchema = z
  .object({
    kind: z.enum(['odrzucona', 'przegrana']).default('odrzucona'),
    reason: z.string().trim().max(500).default(''),
    lostReason: z.enum(keys(LOST_REASONS)).optional(),
    notifyClient: z.boolean().default(false),
    /** treść wiadomości do klienta (bez niej — tekst domyślny) */
    clientMessage: z.string().trim().max(2000).default(''),
  })
  .superRefine((v, ctx) => {
    if (v.kind === 'przegrana' && !v.lostReason) ctx.addIssue({ code: 'custom', path: ['lostReason'], message: 'Wybierz powód przegranej' });
    if (v.kind === 'odrzucona' && v.reason.length < 3) ctx.addIssue({ code: 'custom', path: ['reason'], message: 'Podaj powód' });
  });
export type CaseCloseInput = z.infer<typeof caseCloseSchema>;
/** Zamknięcie sprawy jako zrealizowanej: zwykłe (warunki spełnione) albo wyjątkowe — z uzasadnieniem (docs/10 §3.7). */
export const caseCompleteSchema = z.object({ note: optStr(500), exceptional: z.boolean().default(false), reason: z.string().trim().max(2000).default('') });

export const scopeAddSchema = z.object({
  tradeCode: str(4).min(2),
  companyId: z.string().uuid(),
  description: optStr(2000),
});
export const scopeUpdateSchema = z.object({ description: optStr(2000) });
export const scopeCompanySchema = z.object({ companyId: z.string().uuid(), reason: str(500).min(3, 'Podaj powód zmiany firmy') });
export const scopeStageSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('przegrany'), reason: z.enum(keys(LOST_REASONS)), note: optStr(500) }),
  z.object({ action: z.literal('wstrzymaj'), until: date, note: optStr(500) }),
  z.object({ action: z.literal('wznow'), note: optStr(500) }),
]);
export type ScopeStageInput = z.infer<typeof scopeStageSchema>;


export const noteAddSchema = z.object({ kind: z.enum(keys(NOTE_KINDS)).default('notatka'), body: str(4000).min(1, 'Treść notatki jest pusta') });


/** Nowa branża: kod (2–4 wielkie litery lub cyfry, np. „GR”) — widoczny w skrótach i numeracji wzorów protokołów. */
export const tradeCreateSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{2,4}$/, 'Kod: 2–4 wielkie litery lub cyfry, np. GR'),
  name: str(80).min(2, 'Podaj nazwę branży'),
  kind: z.enum(keys(TRADE_KINDS)),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Kolor w formacie #RRGGBB'),
  defaultCompanyId: z.string().uuid().nullable(),
});

export const tradeUpdateSchema = z.object({
  name: str(80).min(2),
  kind: z.enum(keys(TRADE_KINDS)),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Kolor w formacie #RRGGBB'),
  defaultCompanyId: z.string().uuid().nullable(),
  active: z.boolean(),
  /** kategorie dokumentów wymaganych w sprawie z tą branżą (docs/10 §3.7) — brak w żądaniu: bez zmian */
  requiredDocuments: z.array(z.enum(['projekt', 'dokumentacja', 'zdjecia', 'korespondencja', 'dotacja', 'inne'])).max(6).optional(),
});


export const caseVerifySchema = z.object({ note: z.string().trim().max(1000).default('') }).default({ note: '' });
export const caseReopenSchema = z.object({ reason: z.string().trim().max(1000).default('') }).default({ reason: '' });

export const listQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
